import { useEffect, useRef, useCallback } from "react";

export interface UseDebounceOptions<T extends (...args: any[]) => any> {
  callback: T;
  delay?: number;
  leading?: boolean;
}

export function useDebounce<T extends (...args: any[]) => any>({
  callback,
  delay = 300,
  leading = false,
}: UseDebounceOptions<T>) {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const callbackRef = useRef(callback);
  const isLeadingCallRef = useRef(true);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const debouncedFunction = useCallback(
    (...args: Parameters<T>) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      if (leading && isLeadingCallRef.current) {
        callbackRef.current(...args);
        isLeadingCallRef.current = false;
      } else {
        timerRef.current = setTimeout(() => {
          callbackRef.current(...args);
          isLeadingCallRef.current = true;
        }, delay);
      }
    },
    [delay, leading],
  );

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    isLeadingCallRef.current = true;
  }, []);

  const flush = useCallback(() => {
    cancel();
    if (callbackRef.current) {
      callbackRef.current();
    }
  }, [cancel]);

  useEffect(() => {
    return () => {
      cancel();
    };
  }, [cancel]);

  return {
    debouncedFunction,
    cancel,
    flush,
    isPending: () => timerRef.current !== null,
  };
}
